import { handleGlobalLoader } from 'store/auth/authSlice';
import { ReactComponent as ReceiptIcon } from 'assets/images/receipt.svg';
import { ReactComponent as FaceIcon } from 'assets/images/face.svg';
import { ReactComponent as CreditCardIcon } from 'assets/images/credit-card.svg';
import { rupeeFormatter } from 'central-utils/currencyUtils';
import { convertToCapitalizedString, taxChargesFormat } from 'central-utils/stringUtils';
import { transferAllSessionToLocal } from 'central-utils/storageUtils';
import CustomLoader from 'components/common/CustomLoader';
import CustomToast from 'components/CustomToast';
import { useCheckoutPusherChannel } from 'context/pusher/checkout';
import usePreventBackNavigation from 'hooks/usePreventBackNavigation';
import React, { useEffect, useState } from 'react';
import {
  useGetOrderBillingByOrderIdQuery,
  useRequestBillMutation,
  useCreatePaymentSessionMutation,
} from 'store/checkout/checkoutAPISlice';
import { handleBillDetails } from 'store/checkout/checkoutSlice';
import { useAppDispatch, useAppSelector } from 'store/customHooks';
import { IObject } from 'types';
import { useNavigate } from 'react-router-dom';

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate();

  const initialAPICallSuccess = useAppSelector((state) => state.auth.initialAPISuccess);
  const initialOrderDetails = useAppSelector((state) => state.tableStatusAndOrder.orderDetails);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [billRequested, setBillRequested] = useState<boolean>(false);

  const orderId = sessionStorage.getItem('OrderId');
  const customerDetails = JSON.parse(sessionStorage.getItem('CustomerDetails') || '{}');

  const branchDataStr = sessionStorage.getItem('Branch');
  const branchData = branchDataStr ? JSON.parse(branchDataStr) : null;
  const isBranchPaymentEnabled = branchData?.branchId === 'BAN-BBWST-KMH';

  usePreventBackNavigation('/check-out');

  const dispatch = useAppDispatch();

  const checkoutPusherData = useCheckoutPusherChannel();

  const [requestBill] = useRequestBillMutation();
  const [createPaymentSession] = useCreatePaymentSessionMutation();

  const currentOrderId = String(initialOrderDetails?.id || orderId);

  const [sendEvent] = useState(() => {
    if (!currentOrderId || currentOrderId === 'undefined' || currentOrderId === 'null') {
      return true;
    }
    const hasVisited = sessionStorage.getItem(`checkoutVisited_${currentOrderId}`);
    if (!hasVisited) {
      sessionStorage.setItem(`checkoutVisited_${currentOrderId}`, 'true');
      return true;
    }
    return false;
  });

  const {
    data: orderBillData,
    isLoading: orderBillIsLoading,
    isError: orderBillIsError,
    isSuccess: orderBillIsSuccess,
    isFetching: orderBillIsFetching,
    isUninitialized: orderBillIsUninitialized,
    refetch: refetchOrderBill,
  } = useGetOrderBillingByOrderIdQuery(
    {
      orderId: currentOrderId,
      customerId: String(customerDetails?.id),
      sendEvent: sendEvent,
    },
    {
      skip: !initialAPICallSuccess || !(initialOrderDetails?.id || orderId) || !customerDetails?.id,
    },
  );

  useEffect(() => {
    if (orderBillIsError) {
      setToastMessage('Something went wrong while fetching bill data. Please try again!');
    }
  }, [orderBillIsError]);

  // Re-fetch billing data when admin applies a discount (real-time via Pusher)
  useEffect(() => {
    if (checkoutPusherData?.orderStatus === 'DISCOUNT_UPDATED') {
      refetchOrderBill();
    }
  }, [checkoutPusherData]);

  useEffect(() => {
    if (orderBillData?.status === 'REQUEST_BILL') {
      setBillRequested(true);
    }
  }, [orderBillData]);

  const handleCheckoutTableData = (data: any) => {
    return data?.map((item: any) => {
      const servingSize = item?.servingSizeName?.length ? `${' '}(${item?.servingSizeName})` : '';
      return {
        name: convertToCapitalizedString(item?.itemName) + servingSize,
        quantity: item?.quantity,
        amount: item?.price,
      };
    });
  };
  const transformOrderBillAPIAddData = (data: any) => {
    let charges =
      data?.charges?.map((obj: any) => ({
        name: convertToCapitalizedString(obj?.charges?.name),
        percentage: obj?.rate,
        amount: obj?.amount,
      })) ?? [];

    let tax =
      data?.taxSummary?.map((obj: any) => ({
        name: taxChargesFormat(obj?.tax?.name),
        percentage: obj?.rate,
        amount: obj?.amount,
      })) ?? [];

    const finalData =
      data?.roundOff > 0
        ? [...charges, ...tax, { name: 'Round Off', percentage: 0, amount: data?.roundOff }]
        : [...charges, ...tax];

    return finalData;
  };

  const [convertedAPIData, setConvertedAPIData] = useState<IObject[] | null>(null);
  const [chargesAdded, setChargesAdded] = useState<IObject[] | null>(null);

  useEffect(() => {
    setChargesAdded([]);
    if (orderBillData && Object.keys(orderBillData).length > 0) {
      const storeBillInfo = {
        billNo: orderBillData?.orderId,
        charges: orderBillData?.charges,
        payableAmount: orderBillData?.payableAmount,
        roundoff: orderBillData?.roundoff,
        subtotal: orderBillData?.subtotal,
        taxSummary: orderBillData?.taxSummary,
        totalTax: orderBillData?.totalTax,
        totalCharge: orderBillData?.totalCharge,
      };
      sessionStorage.setItem('billInfo', JSON.stringify(storeBillInfo));
      dispatch(handleBillDetails(orderBillData));
      const convertedData = handleCheckoutTableData(orderBillData?.orderItems);
      setConvertedAPIData(convertedData);
      const addedData = transformOrderBillAPIAddData(orderBillData);
      setChargesAdded(addedData);
    }
  }, [dispatch, orderBillData]);

  useEffect(() => {
    if (
      !(orderBillIsLoading && orderBillIsFetching && !orderBillIsUninitialized) &&
      orderBillData &&
      convertedAPIData &&
      orderBillIsSuccess &&
      convertedAPIData &&
      convertedAPIData?.length === 0
    ) {
      setToastMessage('No orders placed !');
    }
  }, [
    convertedAPIData,
    orderBillData,
    orderBillIsFetching,
    orderBillIsLoading,
    orderBillIsSuccess,
    orderBillIsUninitialized,
  ]);

  const handlePayOnline = async () => {
    dispatch(handleGlobalLoader(true));

    // Re-fetch billing data to get the latest payableAmount — a discount may have been
    // applied by the admin after the page loaded, which would cause a mismatch error.
    let latestBillData = orderBillData;
    try {
      const refreshResult = await refetchOrderBill();
      if (refreshResult?.data) {
        latestBillData = refreshResult.data;
      }
    } catch {
      // Fall back to cached data if re-fetch fails
    }

    const payload = {
      order_id: latestBillData?.orderId ?? orderId,
      amount: latestBillData?.payableAmount,
      customer_id: customerDetails?.id ?? '',
      customer_email: customerDetails?.email ?? '',
      customer_phone: customerDetails?.mobile ?? '',
      description: 'Complete your payment',
      first_name: customerDetails?.firstName ?? customerDetails?.name ?? '',
      last_name: customerDetails?.lastName ?? '',
    };

    transferAllSessionToLocal();

    createPaymentSession(payload).then((res: any) => {
      if (res?.data?.data) {
        dispatch(handleGlobalLoader(false));
        const options = {
          ...res.data.data,
          handler: (response: any) => {
            // Store the orderId so payment-status page can poll the API
            sessionStorage.setItem('RazorpayOrderId', latestBillData?.orderId ?? orderId ?? '');
            sessionStorage.setItem('RazorpayPaymentId', response?.razorpay_payment_id ?? '');
            navigate('/payment-status');
          },
        };
        const rzp = new (window as any).Razorpay(options);
        rzp.on('payment.failed', function (response: any) {
          console.error('Payment Failed', response.error);
          setToastMessage(response.error.description || 'Payment Failed');
        });
        rzp.open();
      } else {
        dispatch(handleGlobalLoader(false));
        const errMessage =
          res?.error?.data?.message ??
          res?.error?.error ??
          res?.error?.data?.error ??
          'Something went wrong while connecting to the payment gateway. Please try again!';
        setToastMessage(errMessage);
      }
    });
  };

  const handlePayViaStaff = async () => {
    dispatch(handleGlobalLoader(true));

    // Re-fetch billing data before requesting bill to ensure synchronization
    try {
      await refetchOrderBill();
    } catch {
      // Fall back if re-fetch fails
    }

    requestBill({ orderId: orderId || initialOrderDetails?.id }).then((res: any) => {
      dispatch(handleGlobalLoader(false));
      if (res?.data) {
        setBillRequested(true);
      } else {
        const errMessage =
          res?.error?.data?.message ??
          res?.error?.error ??
          res?.error?.data?.error ??
          'Something went wrong. Please try again!';
        setToastMessage(errMessage);
      }
    });
  };

  // Clear The Toast Message when the toast is removed
  const handleOnToastDismiss = () => {
    setToastMessage(null);
  };

  // Render the toast message if available
  const renderToast = () => {
    if (toastMessage) {
      return <CustomToast message={toastMessage} handleOnToastDismiss={handleOnToastDismiss} />;
    }
    return null;
  };

  return (
    <>
      {(orderBillIsLoading || orderBillIsFetching) && (
        <CustomLoader isLoading={orderBillIsLoading || orderBillIsFetching} />
      )}
      {orderBillIsSuccess && orderBillData ? (
        <div className='checkoutBillDetails pb-[140px]'>
          <div className='paymentContainer !bg-white !rounded-b-none fixed bottom-0 left-0 w-full p-4 shadow-[0_-4px_15px_rgba(0,0,0,0.05)] z-50 !h-auto text-center'>
            {billRequested || orderBillData?.status === 'REQUEST_BILL' ? (
              <>
                <div className='receiptIcon mx-auto flex justify-center'>
                  <ReceiptIcon className='iconColor' />
                </div>
                <div className='waitMessageTitle my-[5px] text-lg font-semibold'>
                  Please Wait ...
                </div>
                <div className='waitMessageText my-[5px] opacity-80'>
                  The waiter will arrive shortly and process your payment
                </div>
                <div className='flex justify-between items-center mt-4 pt-4 border-t border-black/10 totalPayableSummary font-bold'>
                  <div className='text-left'>Total Payable</div>
                  <div className='text-right'>{rupeeFormatter(orderBillData?.payableAmount)}</div>
                </div>
              </>
            ) : (
              <>
                <div className='flex justify-between items-center mb-4 font-bold'>
                  <div className='text-left text-gray-900 text-xl'>Total Payable</div>
                  <div className='text-right text-gray-900 text-xl'>
                    {rupeeFormatter(orderBillData?.payableAmount)}
                  </div>
                </div>
                <div className='flex flex-row gap-3 pt-2'>
                  <button
                    onClick={handlePayViaStaff}
                    className='receiptIcon flex-1 payOfflineButton py-[20px] rounded-full font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity'
                  >
                    <FaceIcon className='iconColor' />
                    Pay Offline
                  </button>
                  {isBranchPaymentEnabled && (
                    <button
                      onClick={handlePayOnline}
                      className='receiptIcon flex-1 payOnlineButton py-[20px] rounded-full font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-sm text-white'
                    >
                      <CreditCardIcon />
                      Pay Online
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          <div className='billDetailsTitle my-[20px] font-bold text-lg text-gray-800'>
            Bill Details & Summary
          </div>
          <div className='max-w-4xl billTableContainer'>
            <table className='billTable'>
              <thead>
                <tr className='headerRow text-left px-4'>
                  <th className='py-2'>Item</th>
                  <th className='py-2 text-center'>Qty</th>
                  <th className='py-2 text-right'>Amount</th>
                </tr>
              </thead>
              <tbody>
                {/** Main Data Rows */}
                {convertedAPIData && convertedAPIData?.length
                  ? convertedAPIData?.map((row: any, index: number) => (
                      <tr
                        key={index}
                        className={`dataRow ${index % 2 === 0 ? 'oddRow' : 'evenRow'}`}
                      >
                        <td className='py-2'>{row.name}</td>
                        <td className='py-2 text-center'>{row.quantity}</td>
                        <td className='py-2 text-right'>{rupeeFormatter(row.amount)}</td>
                      </tr>
                    ))
                  : null}

                {orderBillData?.discountAmount !== null && orderBillData?.discountAmount > 0 && (
                  <tr key={orderBillData?.discountAmount} className='discountRow'>
                    <td className='py-2' colSpan={2}>
                      Discount
                    </td>
                    <td className='py-2  text-right'>
                      {rupeeFormatter(orderBillData?.discountAmount)}
                    </td>
                  </tr>
                )}
                {orderBillData?.subTotal !== null && orderBillData?.subTotal > 0 && (
                  <tr
                    key={orderBillData?.subTotal}
                    className={`subtotalRow ${
                      orderBillData?.discountAmount ? '' : 'noDiscountRow'
                    }`}
                  >
                    <td className='py-2' colSpan={2}>
                      Sub Total
                    </td>
                    <td className='py-2  text-right'>{rupeeFormatter(orderBillData?.subTotal)}</td>
                  </tr>
                )}

                {/* Charges Added Rows */}
                {chargesAdded && chargesAdded?.length
                  ? chargesAdded?.map((row: any, index: number) => (
                      <tr
                        key={index}
                        className={`chargesAddedRow
                                ${index === 0 ? 'chargesAddedRowFirstChild' : ''}`}
                      >
                        <td className='py-2 text-left' colSpan={2}>
                          {row?.name} {row?.percentage !== 0 ? `(${row?.percentage}%)` : ''}
                        </td>
                        <td className='py-2 text-right'>{rupeeFormatter(row?.amount)}</td>
                      </tr>
                    ))
                  : null}
              </tbody>
              <tfoot>
                <tr className='totalRow'>
                  <td className='py-2' colSpan={2}>
                    Total Payable
                  </td>
                  <td className='py-2 text-right'>
                    {rupeeFormatter(orderBillData?.payableAmount)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      ) : null}
      {renderToast()}
    </>
  );
};

export default CheckoutPage;
